// client/src/components/configuracoes/sinais/SinaisPage.js
import React, { Fragment, useState, useCallback } from 'react';
import { PlusCircle, Loader2, Banknote, XCircle, Eye, Trash2, Power } from 'lucide-react';
import { useSinais, useMutateSinais } from '../../../hooks/useSinais';
import SinalForm from './SinalForm';
import SinalRow from './SinalRow';
import PageHeader from '../../layout/PageHeader';
import FabButton from '../../common/FabButton';
import ActionsDrawer from '../../common/ActionsDrawer';

const SinaisPage = () => {
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [currentSinal, setCurrentSinal] = useState(null);
    const [selectedSinal, setSelectedSinal] = useState(null);

    const { data, isLoading, refetch } = useSinais({ includeInactive: 'true' });
    const { deleteSinal, toggleAtivoSinal, isPending } = useMutateSinais();

    const sinais = data?.data || [];

    const handleAdicionarClick = useCallback(() => {
        setCurrentSinal(null);
        setIsFormVisible(true);
    }, []);

    const handleCloseForm = useCallback(() => {
        setIsFormVisible(false);
        setCurrentSinal(null);
    }, []);

    const handleSaveSuccess = useCallback(() => {
        handleCloseForm();
        refetch();
    }, [handleCloseForm, refetch]);

    const handleEditClick = useCallback((sinal) => {
        setCurrentSinal(sinal);
        setIsFormVisible(true);
        setSelectedSinal(null);
    }, []);

    const handleDelete = useCallback((sinalId) => {
        if (!sinalId) return;
        if (window.confirm('Tem certeza que deseja excluir esta regra de sinal?')) {
            deleteSinal.mutate(sinalId, { onSuccess: () => setSelectedSinal(null) });
        }
    }, [deleteSinal]);

    const handleToggleAtivo = useCallback((sinalId) => {
        if (!sinalId) return;
        toggleAtivoSinal.mutate(sinalId, { onSuccess: () => setSelectedSinal(null) });
    }, [toggleAtivoSinal]);

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Banknote />}
                    title="Regras de Sinal"
                    subtitle={`${sinais.length} regra(s)`}
                    actions={isFormVisible && (
                        <button onClick={handleCloseForm} className="action-button action-button-secondary">
                            <XCircle size={18} className="icon" /> Fechar
                        </button>
                    )}
                />

                {isFormVisible && (
                    <div className="content-panel">
                        <SinalForm
                            key={currentSinal?._id || currentSinal?.id || 'new'}
                            onCloseForm={handleCloseForm}
                            onSaveSuccess={handleSaveSuccess}
                            current={currentSinal}
                        />
                    </div>
                )}
            </div>

            {!isFormVisible && (
                <div className="table-container">
                    <div className="table-wrapper">
                        {isLoading ? (
                            <div className="table-loading-state">
                                <Loader2 size={24} className="animate-spin" />
                                <span>Carregando regras de sinal...</span>
                            </div>
                        ) : sinais.length > 0 ? (
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>Tipo</th>
                                        <th>Alvo</th>
                                        <th>Sinal</th>
                                        <th>Vigência</th>
                                        <th>Status</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sinais.map((sinal) => (
                                        <SinalRow
                                            key={sinal._id || sinal.id}
                                            sinal={sinal}
                                            onOpenActions={setSelectedSinal}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="table-empty-state">
                                <Banknote size={48} />
                                <h3>Nenhuma regra de sinal cadastrada</h3>
                                <p>Clique em "Adicionar Regra" para exigir sinal ao agendar um serviço ou recurso.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <FabButton
                onClick={handleAdicionarClick}
                title="Adicionar Regra"
                visible={!isFormVisible}
                disabled={isLoading}
                icon={<Banknote size={26} className="icon" />}
            />

            <ActionsDrawer
                open={!!selectedSinal}
                onClose={() => setSelectedSinal(null)}
                title={selectedSinal?.nome || 'Regra de Sinal'}
                actions={selectedSinal ? [
                    {
                        key: 'editar',
                        label: 'Visualizar/Editar',
                        icon: <Eye size={20} className="icon" />,
                        onClick: () => handleEditClick(selectedSinal),
                    },
                    {
                        key: 'alternar',
                        label: selectedSinal.ativo ? 'Desativar' : 'Ativar',
                        icon: <Power size={20} className="icon" />,
                        onClick: () => handleToggleAtivo(selectedSinal._id || selectedSinal.id),
                        loading: isPending,
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedSinal._id || selectedSinal.id),
                        destructive: true,
                        loading: isPending,
                    },
                ] : []}
            />
        </Fragment>
    );
};

export default SinaisPage;
